import { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  IconButton,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import BlockIcon from "@mui/icons-material/Block";
import CheckIcon from "@mui/icons-material/Check";
import RefreshIcon from "@mui/icons-material/Refresh";

import {
  addUserToGroup,
  ApiError,
  disableUser,
  listUsers,
  removeUserFromGroup,
} from "./api";
import type { AdminUser, UserGroup } from "./types";
import { SectionLabel, colors } from "./theme";

const GROUPS: UserGroup[] = ["helper-agents", "supervisors", "admins"];

// Admin-only page. Tabs are gated on `admins` group in App.tsx; the API
// itself enforces the same gate independently.
export default function UsersPage() {
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pendingAction, setPendingAction] = useState<string | null>(null);
  const [selectedGroup, setSelectedGroup] = useState<Record<string, UserGroup | "">>({});

  async function refresh() {
    setLoading(true);
    setError(null);
    try {
      const res = await listUsers();
      setUsers(res.users);
    } catch (e) {
      setError(e instanceof ApiError ? `${e.status}: ${e.body}` : String(e));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function handleAdd(user: AdminUser) {
    const g = selectedGroup[user.username];
    if (!g) return;
    setPendingAction(`${user.username}:add:${g}`);
    try {
      await addUserToGroup(user.username, g as UserGroup);
      setSelectedGroup((s) => ({ ...s, [user.username]: "" }));
      await refresh();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.status}: ${e.body}` : String(e));
    } finally {
      setPendingAction(null);
    }
  }

  async function handleRemove(user: AdminUser, group: string) {
    if (!confirm(`Remove ${user.email ?? user.username} from "${group}"?`)) return;
    setPendingAction(`${user.username}:remove:${group}`);
    try {
      await removeUserFromGroup(user.username, group as UserGroup);
      await refresh();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.status}: ${e.body}` : String(e));
    } finally {
      setPendingAction(null);
    }
  }

  async function handleDisable(user: AdminUser) {
    if (!confirm(`Disable ${user.email ?? user.username}? They won't be able to sign in.`)) return;
    setPendingAction(`${user.username}:disable`);
    try {
      await disableUser(user.username);
      await refresh();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.status}: ${e.body}` : String(e));
    } finally {
      setPendingAction(null);
    }
  }

  const pending = users.filter((u) => u.enabled && u.groups.length === 0);
  const active = users.filter((u) => u.enabled && u.groups.length > 0);
  const disabled = users.filter((u) => !u.enabled);

  const tableProps = {
    selectedGroup, setSelectedGroup,
    onAdd: handleAdd, onRemove: handleRemove, onDisable: handleDisable,
    pendingAction,
  };

  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
        <Typography variant="h6">User management</Typography>
        <Box sx={{ flexGrow: 1 }} />
        <IconButton size="small" onClick={refresh} disabled={loading} aria-label="Refresh">
          <RefreshIcon />
        </IconButton>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <UserTable
        title={`Pending approval (${pending.length})`}
        users={pending}
        emptyText="No pending users right now."
        {...tableProps}
      />

      <Box sx={{ mt: 3 }}>
        <UserTable
          title={`Active (${active.length})`}
          users={active}
          emptyText="No users have been approved yet."
          {...tableProps}
        />
      </Box>

      {disabled.length > 0 && (
        <Box sx={{ mt: 3, opacity: 0.6 }}>
          <UserTable
            title={`Disabled (${disabled.length})`}
            users={disabled}
            emptyText="None."
            {...tableProps}
          />
        </Box>
      )}
    </Box>
  );
}

function UserTable(props: {
  title: string;
  users: AdminUser[];
  emptyText: string;
  selectedGroup: Record<string, UserGroup | "">;
  setSelectedGroup: React.Dispatch<React.SetStateAction<Record<string, UserGroup | "">>>;
  onAdd: (u: AdminUser) => void;
  onRemove: (u: AdminUser, group: string) => void;
  onDisable: (u: AdminUser) => void;
  pendingAction: string | null;
}) {
  const { title, users, emptyText, selectedGroup, setSelectedGroup, onAdd, onRemove, onDisable, pendingAction } = props;

  return (
    <Box>
      <SectionLabel>{title}</SectionLabel>
      <Table
        size="small"
        sx={{
          mt: 1,
          background: "white",
          border: `1px solid ${colors.gray200}`,
          borderRadius: 1,
        }}
      >
        <TableHead>
          <TableRow>
            <TableCell>Email</TableCell>
            <TableCell>Status</TableCell>
            <TableCell>Groups</TableCell>
            <TableCell>Add to group</TableCell>
            <TableCell></TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {users.length === 0 ? (
            <TableRow>
              <TableCell colSpan={5} sx={{ textAlign: "center", fontStyle: "italic", color: colors.gray500 }}>
                {emptyText}
              </TableCell>
            </TableRow>
          ) : (
            users.map((u) => (
              <TableRow key={u.username}>
                <TableCell>{u.email ?? u.username}</TableCell>
                <TableCell>
                  {u.enabled ? (
                    <Chip label={u.status ?? "—"} size="small" />
                  ) : (
                    <Chip label="DISABLED" size="small" color="error" />
                  )}
                </TableCell>
                <TableCell>
                  <Stack direction="row" spacing={0.5} flexWrap="wrap">
                    {u.groups.length === 0 ? (
                      <Chip label="pending" size="small" variant="outlined" />
                    ) : (
                      u.groups.map((g) => (
                        <Chip
                          key={g}
                          label={g}
                          size="small"
                          color={g === "admins" ? "primary" : g === "supervisors" ? "secondary" : "default"}
                          onDelete={u.enabled ? () => onRemove(u, g) : undefined}
                          disabled={pendingAction === `${u.username}:remove:${g}`}
                        />
                      ))
                    )}
                  </Stack>
                </TableCell>
                <TableCell>
                  {u.enabled && (
                    <Stack direction="row" spacing={1}>
                      <Select
                        size="small"
                        value={selectedGroup[u.username] ?? ""}
                        onChange={(e) =>
                          setSelectedGroup((s) => ({ ...s, [u.username]: e.target.value as UserGroup | "" }))
                        }
                        displayEmpty
                        sx={{ minWidth: 150 }}
                      >
                        <MenuItem value="">
                          <em>group…</em>
                        </MenuItem>
                        {GROUPS.filter((g) => !u.groups.includes(g)).map((g) => (
                          <MenuItem key={g} value={g}>
                            {g}
                          </MenuItem>
                        ))}
                      </Select>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<CheckIcon />}
                        onClick={() => onAdd(u)}
                        disabled={
                          !selectedGroup[u.username] ||
                          pendingAction?.startsWith(`${u.username}:add:`) === true
                        }
                      >
                        Add
                      </Button>
                    </Stack>
                  )}
                </TableCell>
                <TableCell align="right">
                  {u.enabled && (
                    <Button
                      size="small"
                      color="error"
                      startIcon={<BlockIcon />}
                      onClick={() => onDisable(u)}
                      disabled={pendingAction === `${u.username}:disable`}
                    >
                      Disable
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </Box>
  );
}
